import React, {useEffect, useRef, useState} from "react";
import Snackbar from '@mui/material/Snackbar';
import Slide from '@mui/material/Slide';
import Alert from "@mui/material/Alert";
import {useDispatch, useSelector} from "react-redux";
import {useIntl} from "react-intl";
import {shiftErrorToast} from "../../../features/error/errorToastSlice";
import {ErrorToastItem} from "../../../features/error/errorToastSlice";

/**
 * Global snackbar host for backend errors (CustomException…).
 *
 * Errors detected by the axios interceptor are pushed into the `errorToast`
 * redux queue (see src/helpers/backendErrorHandler.ts) and displayed here,
 * once, at the application root — whatever page caused the HTTP call.
 *
 * Mounted in src/app/index.tsx inside the IntlProvider / ThemeProvider so the
 * message is translated with the language currently chosen by the user.
 */
function GlobalErrorSnackbar() {
    const dispatch = useDispatch();
    const intl = useIntl();
    const queue: ErrorToastItem[] = useSelector((state: any) => state.errorToast?.queue ?? []);
    const current: ErrorToastItem | undefined = queue[0];
    const [open, setOpen] = useState(false);
    const closingTimer = useRef<any>(null);

    useEffect(() => {
        if (current) {
            setOpen(true);
        }
    }, [current?.id]);

    useEffect(() => {
        return () => {
            if (closingTimer.current) {
                clearTimeout(closingTimer.current);
            }
        };
    }, []);

    const close = () => {
        if (!open) {
            return;
        }
        setOpen(false);
        // Let the exit transition finish before dequeuing the next toast.
        closingTimer.current = setTimeout(() => {
            if (current) {
                dispatch(shiftErrorToast());
            }
        }, 250);
    };

    const handleClose = (_event: any, reason: any) => {
        if (reason === 'clickaway') {
            return;
        }
        close();
    };

    if (!current) {
        return null;
    }

    const message = current.messageId
        ? intl.formatMessage({id: current.messageId}, {defaultMessage: current.message ?? ''})
        : (current.message ?? '');

    return (
        <Snackbar
            key={current.id}
            anchorOrigin={{
                vertical: "top",
                horizontal: "right"
            }}
            autoHideDuration={current.duration ?? 6000}
            open={open}
            onClose={handleClose}
            TransitionComponent={Slide}
            sx={{marginTop: 6}}
        >
            <Alert variant="filled" severity={current.severity ?? 'error'}>
                {message}
            </Alert>
        </Snackbar>
    );
}

export default GlobalErrorSnackbar;
